import type { ParseDiagnostic } from './diagnostics.js';

/**
 * Contract 4 of docs/plans/41-cross-cutting-contracts.md.
 *
 * Produced by the parser (M2 stage E), consumed by the publish UI (M5) before a
 * publish runs, and serialised into the published package so "what did we lose"
 * stays answerable six months later without re-parsing the deck.
 */
export type FidelityStrategy = 'Vector' | 'Hybrid' | 'Raster';

export interface SlideFidelity {
  readonly slideIndex: number;
  readonly strategy: FidelityStrategy;
  /**
   * Why this strategy was chosen. Never empty for Hybrid or Raster — an empty
   * reason list means the selector guessed, which is a defect, and
   * `assertFidelityReportValid` fails on it.
   */
  readonly reasons: readonly string[];
  /** OOXML constructs silently dropped on this slide. Feeds the fidelity-debt table. */
  readonly droppedConstructs: readonly string[];
  readonly warnings: readonly string[];
}

export interface FontSubstitution {
  readonly requested: string;
  readonly substitutedWith: string;
  readonly reason: string;
  readonly affectedSlides: readonly number[];
}

export interface ExternalDependency {
  readonly kind: 'youtube' | 'vimeo' | 'web' | 'other';
  readonly url: string;
  readonly slideIndex: number;
  readonly hasStaticFallback: boolean;
}

export interface FidelityReport {
  readonly generatedAt: string;
  readonly courseVersionHash: string;
  readonly slides: readonly SlideFidelity[];
  readonly fonts: readonly FontSubstitution[];
  readonly externalDependencies: readonly ExternalDependency[];
  readonly diagnostics: readonly ParseDiagnostic[];
  /** Blocking entries stop the publish. Everything else only warns. */
  readonly blocking: readonly ParseDiagnostic[];
}

export function emptyFidelityReport(courseVersionHash = ''): FidelityReport {
  return {
    generatedAt: new Date(0).toISOString(),
    courseVersionHash,
    slides: [],
    fonts: [],
    externalDependencies: [],
    diagnostics: [],
    blocking: [],
  };
}

export class FidelityReportInvalid extends Error {}

/**
 * Enforces the two rules the contract calls out explicitly: a non-Vector slide
 * must say why, and anything in `blocking` must actually be blocking severity.
 */
export function assertFidelityReportValid(report: FidelityReport): void {
  for (const slide of report.slides) {
    if (slide.strategy !== 'Vector' && slide.reasons.length === 0) {
      throw new FidelityReportInvalid(
        `Slide ${slide.slideIndex} chose strategy ${slide.strategy} without recording a reason.`,
      );
    }
  }
  for (const d of report.blocking) {
    if (d.severity !== 'blocking') {
      throw new FidelityReportInvalid(
        `Diagnostic ${d.code} is listed as blocking but has severity ${d.severity}.`,
      );
    }
  }
}

export function canPublish(report: FidelityReport): boolean {
  return report.blocking.length === 0;
}

/**
 * Folds constructs the render compiler had to drop back into the report.
 *
 * The parser chooses a strategy from the IR alone, but some losses only surface
 * at layout time — an unimplemented preset shape, a pattern fill. Recording
 * those in `droppedConstructs` without revisiting `strategy` would leave a slide
 * full of wrong rectangles still claiming to be a faithful Vector rendering,
 * which is the opposite of what this report is for.
 *
 * This lives here rather than in the parser because both the parser and the
 * publisher need it, and a second copy of the escalation rule would drift.
 */
export function applyCompileDrops(
  report: FidelityReport,
  droppedBySlideIndex: ReadonlyMap<number, readonly string[]>,
): FidelityReport {
  return {
    ...report,
    slides: report.slides.map((slide) => {
      const extra = droppedBySlideIndex.get(slide.slideIndex) ?? [];
      if (extra.length === 0) return slide;

      const reasons = [...slide.reasons];
      let strategy = slide.strategy;
      for (const construct of extra) {
        const reason = reasonForDrop(construct);
        if (!reason) continue;
        reasons.push(reason);
        if (strategy === 'Vector') strategy = 'Hybrid';
      }

      return {
        ...slide,
        strategy,
        reasons: [...new Set(reasons)],
        droppedConstructs: [...new Set([...slide.droppedConstructs, ...extra])].sort(),
      };
    }),
  };
}

/** Plain-language explanation for a dropped construct, or undefined if it is cosmetic. */
function reasonForDrop(construct: string): string | undefined {
  if (construct.startsWith('geometry:')) {
    return `The shape "${construct.slice('geometry:'.length)}" is drawn as a rectangle.`;
  }
  if (construct.startsWith('fill:pattern:')) {
    return 'A pattern fill is drawn as a flat colour.';
  }
  if (construct === 'bullet:picture') {
    return 'A picture bullet is drawn as a plain bullet character.';
  }
  if (construct === 'text:wordArt') {
    return 'Text effects are approximated in the browser.';
  }
  if (construct.startsWith('graphicFrame:')) {
    return `A ${construct.slice('graphicFrame:'.length)} is drawn from its fallback image.`;
  }
  if (construct === 'picture:linked-not-embedded') {
    return 'A linked picture is not stored in the file.';
  }
  if (construct.startsWith('animation:filter:')) {
    return `The animation effect "${construct.slice('animation:filter:'.length)}" is played as a fade.`;
  }
  if (construct.startsWith('animation:effect:')) {
    return 'An animation effect had no readable behaviour and does not play.';
  }
  if (construct.startsWith('animation:attribute:')) {
    return `The animated property "${construct.slice('animation:attribute:'.length)}" is not animated.`;
  }
  if (construct.startsWith('animation:motion:')) {
    return 'Part of a motion path could not be read, so the movement is shortened.';
  }
  if (construct.startsWith('animation:behavior:')) {
    return `An animation behaviour (${construct.slice('animation:behavior:'.length)}) does not play.`;
  }
  if (construct === 'animation:repeat:indefinite') {
    return 'An animation set to repeat forever plays once, so the slide can finish.';
  }
  if (construct === 'animation:target:unresolved') {
    return 'An animation targets something that is not on the slide, so it does not play.';
  }
  if (construct.startsWith('animation:')) {
    return 'Part of the slide animation could not be represented.';
  }
  return undefined;
}

/** Rolls the per-slide dropped constructs up into the deck-wide fidelity debt list. */
export function fidelityDebt(report: FidelityReport): { construct: string; slides: number[] }[] {
  const map = new Map<string, number[]>();
  for (const slide of report.slides) {
    for (const construct of slide.droppedConstructs) {
      const list = map.get(construct);
      if (list) list.push(slide.slideIndex);
      else map.set(construct, [slide.slideIndex]);
    }
  }
  return [...map.entries()]
    .map(([construct, slides]) => ({ construct, slides }))
    .sort((a, b) => a.construct.localeCompare(b.construct));
}
